import React, { useState } from 'react';
import { useRefreshSingleMealAndGroceryList } from '../hooks/refreshSingleMealAndGroceryList';
import { useAuth } from '../context/AuthContext';
import { fetchWithCSRF } from '../utils/csrfUtils';

function EmptyMealCard({ meal, onMealRefresh, onGroceryListRefresh, isInPast, isNewSlot, mealTime, onAddNewMeal }) {
    const { refreshSingleMealAndGroceryList, isRefreshing } = useRefreshSingleMealAndGroceryList();
    const [errorRefreshingMeal, setErrorRefreshingMeal] = useState(false);
    const [errorRefreshingMealMessage, setErrorRefreshingMealMessage] = useState('');
    const [isAddingNewMeal, setIsAddingNewMeal] = useState(false);
    const { sessionToken } = useAuth();

    const showErrorRefreshingMealToast = (message) => {
        setErrorRefreshingMealMessage(message);
        setErrorRefreshingMeal(true);
        
        setTimeout(() => {
            setErrorRefreshingMeal(false);
        }, 5000);
    };

    const handleAddRecipe = async (e) => {
        e.stopPropagation();
        
        if (isInPast) {
            return;
        }

        // If this is a new slot card, call the add new meal function
        if (isNewSlot && onAddNewMeal && mealTime) {
            try {
                setIsAddingNewMeal(true);
                await onAddNewMeal(mealTime);
                setIsAddingNewMeal(false);
            } catch (error) {
                console.error('Failed to add new meal:', error);
                setIsAddingNewMeal(false);
                showErrorRefreshingMealToast('Error adding meal. Please refresh the page and try again.');
            }
            return;
        }

        // Otherwise, this is an existing empty slot, so add a recipe to it
        try {
            await refreshSingleMealAndGroceryList(meal.id, meal.app_user_id);
            
            if (onMealRefresh) {
                onMealRefresh();
            }
            
            if (onGroceryListRefresh) {
                onGroceryListRefresh("Grocery list updated");
            }
        } catch (error) {
            console.error('Failed to add recipe:', error);
            showErrorRefreshingMealToast('Error adding recipe. Please refresh the page and try again.');
        }
    };

    const handleRemoveSlot = async (e) => {
        e.stopPropagation();
        
        if (isInPast || isNewSlot) {
            return;
        }

        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            const response = await fetchWithCSRF('/api/v1/meal/delete_no_refresh', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({ 
                    meal_id: meal.id,
                }),
            });
            
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to remove meal slot');
            }

            if (onMealRefresh) {
                onMealRefresh();
            }
            
            if (onGroceryListRefresh) {
                onGroceryListRefresh("Meal slot removed");
            }
        } catch (error) {
            console.error('Failed to remove meal slot:', error);
            showErrorRefreshingMealToast('Error removing meal slot. Please refresh the page and try again.');
        }
    };

    return (
        <div 
            className={`meal-card empty-meal-card ${isInPast ? 'cooked' : ''}`}
            onClick={handleAddRecipe}
            style={{ 
                cursor: isInPast ? 'not-allowed' : 'pointer',
                opacity: isInPast ? 0.5 : 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                border: '2px dashed #d0d0d0',
                backgroundColor: '#fafafa',
                transition: 'all 0.2s ease',
                position: 'relative'
            }}
            onMouseEnter={(e) => {
                if (!isInPast) {
                    e.currentTarget.style.borderColor = '#52cd92';
                    e.currentTarget.style.backgroundColor = '#f0fdf7';
                }
            }}
            onMouseLeave={(e) => {
                if (!isInPast) {
                    e.currentTarget.style.borderColor = '#d0d0d0';
                    e.currentTarget.style.backgroundColor = '#fafafa';
                }
            }}
        >
            {/* Remove slot button in top left corner - only show for existing slots */}
            {!isInPast && !isNewSlot && (
                <div 
                    className="delete-meal-corner"
                    onClick={handleRemoveSlot}
                    style={{ 
                        position: 'absolute',
                        top: '-8px',
                        left: '-8px',
                        cursor: 'pointer',
                        zIndex: 10,
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        transition: 'opacity 0.2s, transform 0.2s'
                    }}
                >
                    <img 
                        src="/icons/x-outline.svg" 
                        alt="Remove Slot" 
                        style={{ 
                            width: '30px', 
                            height: '30px'
                        }}
                    />
                </div>
            )}
            {(isRefreshing || isAddingNewMeal) ? (
                <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                        width: '80px', 
                        height: '80px', 
                        borderRadius: '50%',
                        border: '3px solid #f0f0f0',
                        borderTopColor: '#52cd92',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 20px'
                    }}></div>
                    <p style={{ color: '#666', fontSize: '0.9rem' }}>
                        {isAddingNewMeal ? 'Adding meal...' : 'Adding recipe...'}
                    </p>
                </div>
            ) : (
                <>
                    <div style={{ 
                        width: '80px', 
                        height: '80px', 
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                        border: '2px solid #d0d0d0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '20px',
                        transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                        if (!isInPast) {
                            e.currentTarget.style.borderColor = '#52cd92';
                            e.currentTarget.querySelector('svg').style.stroke = '#52cd92';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isInPast) {
                            e.currentTarget.style.borderColor = '#d0d0d0';
                            e.currentTarget.querySelector('svg').style.stroke = '#999';
                        }
                    }}
                    >
                        <svg 
                            width="40" 
                            height="40" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="#999" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            style={{ transition: 'all 0.2s ease' }}
                        >
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                    </div>
                    <h3 style={{ 
                        color: '#666', 
                        fontSize: '1.5rem',
                        fontWeight: '500',
                        margin: 0
                    }}>
                        Add meal
                    </h3>
                </>
            )}
        </div>
    );
}

export default EmptyMealCard;

